//Global imports
import { useEffect, useState } from 'react';
import { cilPencil, cilPlus } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import {
    CButton,
    CCard,
    CCardBody,
    CCardFooter,
    CCardHeader,
    CCardText,
    CCardTitle,
    CCol,
} from '@coreui/react';
import { useNavigate } from 'react-router-dom';

//File imports
import { Language } from '../../../../language/English';
import ConfirmationBoxModal from '../../../../components/Layout/ConfirmationBoxModal';
import { DocumentTemplatePojo } from '../../Api/DocumentTemplatePojo';
import {
    DOCUMENT_TEMPLATE_STATUS_ACTIVE,
    DOCUMENT_TEMPLATE_STATUS_INACTIVE,
} from '../../Api/DocumentTemplateConstants';
import { DOCUMENT_TEMPLATE_TRANSLATION } from '../../../../PagesNames';

const DocumentTemplateCard = (props: {
    documentTemplate: DocumentTemplatePojo;
    key: number;
    deleteDocumentTemplate: () => void;
    changeDocumentTemplateStatus: (status: number) => void;
    onEdit: (template: DocumentTemplatePojo) => void;
}) => {
    const data = props.documentTemplate;
    const [deleteTemplateConfirmation, setDeleteTemplateConfirmation] =
        useState<boolean>(false);
    const navigate = useNavigate();
    const [noTranslationsAvailable, setNoTranslationsAvailable] =
        useState<boolean>(true);
    const [availableTranslations, setAvailableTranslations] = useState<
        string[]
    >([]);

    const closeDeleteTemplateConfirmation = () =>
        setDeleteTemplateConfirmation(false);

    const openDeleteTemplateConfirmation = () =>
        getExistsTranslationNameInString();

    const _deleteDocumentTemplate = () => {
        closeDeleteTemplateConfirmation();
        props.deleteDocumentTemplate();
    };

    const changeDocumentTemplateStatus = () => {
        closeDeleteTemplateConfirmation();
        if (data.status === 1) {
            props.changeDocumentTemplateStatus(
                DOCUMENT_TEMPLATE_STATUS_INACTIVE,
            );
        } else {
            props.changeDocumentTemplateStatus(DOCUMENT_TEMPLATE_STATUS_ACTIVE);
        }
    };

    const handleLanguageClick = (language: string, status: number) => {
        navigate(DOCUMENT_TEMPLATE_TRANSLATION, {
            state: {
                language,
                id: data.documentTemplateId,
                isEdit: status,
                name: data.name,
            },
        });
    };

    const getExistsTranslationNameInString = () => {
        type LanguageKey = keyof typeof data.translations;
        const activeLanguages = Object.keys(data?.translations).filter(
            (lang) => data?.translations[lang as LanguageKey] === 1,
        );
        if (activeLanguages.length > 0) {
            setNoTranslationsAvailable(false);
            let translations = activeLanguages.map((lang) => {
                switch (lang) {
                    case 'en':
                        return Language.ENGLISH;
                    case 'fr':
                        return Language.FRENCH;
                    case 'de':
                        return Language.GERMAN;
                    default:
                        return '';
                }
            });
            setAvailableTranslations(translations);
        }
        setDeleteTemplateConfirmation(true);
    };

    return (
        <CCol xs={12} md={4} className="mb-4">
            <CCard className="border-0 shadow-sm rounded-2">
                <CCardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <div
                            className="close-button"
                            onClick={openDeleteTemplateConfirmation}
                        >
                            <span>X</span>
                        </div>
                        <div className={`label-position`}>
                            <div
                                className={`${data.status === 1 ? 'article-label' : 'article-label disable'}`}
                            >
                                {data.status === 1
                                    ? Language.ACTIVE
                                    : Language.INACTIVE}
                            </div>
                        </div>
                    </div>
                </CCardHeader>
                <CCardBody>
                    <div className="d-flex justify-content-between align-items-start my-2">
                        <CCardTitle
                            className="fs-3 text-truncate"
                            style={{ maxWidth: '100%' }}
                        >
                            {data.name}
                        </CCardTitle>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                        <CCardText className="mb-0">
                            <span className="fw-bold">{Language.PRICE}:</span>{' '}
                            {data.price}
                        </CCardText>
                        <CCardText className="mb-0">
                            <span className="fw-bold">
                                {Language.OUTSIDE_PRICE}:
                            </span>{' '}
                            {data.outsidePrice}
                        </CCardText>
                    </div>
                    <CCardText className="mb-3">
                        <span className="fw-bold">
                            {Language.PREPARATION_TIME_}:
                        </span>{' '}
                        {data.preparationTimeInMinutes} {Language.MINUTES}
                    </CCardText>

                    <div className="d-flex gap-2 language-button mb-1">
                        <CButton
                            color="primary"
                            onClick={() =>
                                handleLanguageClick('en', data.translations.en)
                            }
                        >
                            <CIcon
                                icon={
                                    data.translations.en === 0
                                        ? cilPlus
                                        : cilPencil
                                }
                                className="me-2"
                            />
                            {Language.ENGLISH}
                        </CButton>
                        <CButton
                            color="info"
                            onClick={() =>
                                handleLanguageClick('fr', data.translations.fr)
                            }
                        >
                            <CIcon
                                icon={
                                    data.translations.fr === 0
                                        ? cilPlus
                                        : cilPencil
                                }
                                className="me-2"
                            />
                            {Language.FRENCH}
                        </CButton>
                        <CButton
                            color="warning"
                            onClick={() =>
                                handleLanguageClick('de', data.translations.de)
                            }
                        >
                            <CIcon
                                icon={
                                    data.translations.de === 0
                                        ? cilPlus
                                        : cilPencil
                                }
                                className="me-2"
                            />
                            {Language.GERMAN}
                        </CButton>
                    </div>
                </CCardBody>
                <CCardFooter className="bg-white">
                    <div className="d-flex gap-2">
                        <CButton
                            color="primary"
                            size="sm"
                            onClick={() => props.onEdit(data)}
                        >
                            <CIcon icon={cilPencil} className="me-2" />
                            {Language.EDIT_DOCUMENT}
                        </CButton>
                        <CButton
                            onClick={changeDocumentTemplateStatus}
                            color={`${data.status === 1 ? 'secondary' : 'success'}`}
                            className="me-2"
                        >
                            {`${data.status === 1 ? Language.INACTIVATE : Language.ACTIVATE}`}
                        </CButton>
                    </div>
                </CCardFooter>
            </CCard>

            {deleteTemplateConfirmation && (
                <ConfirmationBoxModal
                    onClose={closeDeleteTemplateConfirmation}
                    title={Language.DELETE_DOCUMENT}
                    message={`${Language.DELETE_CONFIRMATION_WITH_DOCUMENT_NAME} ${data.name} ?`}
                    subMessage={
                        !noTranslationsAvailable
                            ? Language.TRANLSATION_DELETE_CONFIRMATION +
                              availableTranslations.join(', ')
                            : ''
                    }
                    buttonOneText={Language.YES}
                    buttonTwoText={Language.CLOSE}
                    buttonOneCallable={_deleteDocumentTemplate}
                    buttonTwoCallable={closeDeleteTemplateConfirmation}
                    buttonOneCallBackFunctionParams={1}
                    buttonTwoCallBackFunctionParams={null}
                />
            )}
        </CCol>
    );
};

export default DocumentTemplateCard;
